'use client';

import { useState, useEffect } from 'react';
import { animated, useSpring } from '@react-spring/web';
import ReactModal from 'react-modal';
import mixpanel from 'mixpanel-browser';
import { useAuth } from '@/context/AuthContext';
import { useJobs } from '@/context/JobsContext';
import { updateJobStage as updateJobStageAPI } from '../../functions';
import styles from './styles.module.css';
import * as Sentry from '@sentry/react';

function StatusModal({ isOpen, onClose, jobData }) {
  const { currentUser } = useAuth();
  const { jobStages, updateJobStage, refreshUserData } = useJobs();
  const [selectedStatus, setSelectedStatus] = useState('None');
  const [saving, setSaving] = useState(false);

  const statusOptions = [
    { label: 'None', value: 'none' },
    { label: 'Applied', value: 'applied' },
    { label: 'Got Interview', value: 'got_interview' },
    { label: 'Got Offer', value: 'got_offer' },
    { label: 'Not Selected', value: 'rejected' },
  ];

  // Set initial status based on current job stage
  useEffect(() => {
    if (jobData?.id && jobStages) {
      const currentStage = Object.keys(jobStages).find((stage) => jobStages[stage].includes(jobData.id));
      const currentOption = statusOptions.find((option) => option.value === currentStage);
      setSelectedStatus(currentOption?.label || 'None');
    }
  }, [jobData?.id, jobStages]);

  // Animation for status modal
  const statusModalSpring = useSpring({
    opacity: isOpen ? 1 : 0,
    transform: `translateY(${isOpen ? 0 : 40}px)`,
  });

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  const handleStatusSave = async () => {
    if (!jobData?.id || !currentUser?.uid) return;

    setSaving(true);
    try {
      const selectedOption = statusOptions.find((option) => option.label === selectedStatus);
      const newStage = selectedOption?.value || 'none';

      // Find current stage
      const currentStage = Object.keys(jobStages).find((stage) => jobStages[stage].includes(jobData.id));

      // Update via Backend API
      const result = await updateJobStageAPI({
        uid: currentUser.uid,
        jobID: jobData.id,
        stage: newStage,
      });

      if (result.success) {
        // Update local context
        updateJobStage(jobData.id, newStage, currentStage);

        mixpanel.track('Application Status Updated', {
          'Opportunity ID': jobData.id,
          'Opportunity Title': jobData.title,
          'Previous Status': currentStage || 'none',
          'New Status': newStage,
        });

        onClose();
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error updating job stage:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={styles.statusModal}
      overlayClassName={styles.statusModalOverlay}
      shouldCloseOnEsc
      closeTimeoutMS={300}
      ariaHideApp={false}
    >
      <animated.div style={statusModalSpring} className={styles.statusModalContent}>
        <div className={styles.statusModalHeader}>
          <h3>Track Application Status</h3>
          <button
            type="button"
            onClick={onClose}
            className={styles.closeButton}
          >
            ×
          </button>
        </div>

        <div className={styles.statusOptions}>
          {statusOptions.map((option) => {
            const inputId = `status-${option.value}`;
            return (
              <label key={option.value} htmlFor={inputId} className={styles.radioOption}>
                <input
                  id={inputId}
                  type="radio"
                  name="applicationStatus"
                  value={option.label}
                  checked={selectedStatus === option.label}
                  onChange={() => handleStatusChange(option.label)}
                  className={styles.radioInput}
                  disabled={saving}
                />
                <span className={styles.radioCustom} />
                <span className={styles.radioLabel}>{option.label}</span>
              </label>
            );
          })}
        </div>

        <div className={styles.statusModalActions}>
          <button
            type="button"
            onClick={onClose}
            className={styles.cancelButton}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleStatusSave}
            className={styles.saveButton}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Status'}
          </button>
        </div>
      </animated.div>
    </ReactModal>
  );
}

export default StatusModal;
